import React, { useState, useEffect } from 'react';
import { Select, Button, InputNumber, message, Spin, Tag, Tabs, Input } from 'antd';
import { SaveOutlined, SettingOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { getAllAdminIECsService } from '../../services/FileDataService';
import { getPageConfigService, savePageConfigService } from '../../services/FileDataService';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;

const REPORT_CONFIGS = [
  {
    page_name: 'TAT-ExportClearance',
    label: 'TAT Export Clearance',
    description: 'Configure day buckets for shipments cleared in X days — export summary.',
    defaultThresholds: [1, 5],
    type: 'TAT',
    accentColor: '#2563eb',
    modes: ['Sea', 'Air', 'Land'],
  },
  {
    page_name: 'TAT-ImportClearance',
    label: 'TAT Import Clearance',
    description: 'Configure day buckets for shipments cleared in X days — import summary.',
    defaultThresholds: [1, 5],
    type: 'TAT',
    accentColor: '#7c3aed',
    modes: ['Sea', 'Air', 'Land'],
  },
];

const chipColors = {
  green: { bg: '#f0fdf4', border: '#bbf7d0', text: '#16a34a' },
  orange: { bg: '#fff7ed', border: '#fed7aa', text: '#ea580c' },
  red: { bg: '#fef2f2', border: '#fecaca', text: '#dc2626' },
  default: { bg: '#f8fafc', border: '#e2e8f0', text: '#64748b' },
};

const PageConfigurator = () => {
  const [selectedIEC, setSelectedIEC] = useState(null);
  const [iecList, setIecList] = useState([]);
  const [loadingIECs, setLoadingIECs] = useState(false);
  const [thresholdsMap, setThresholdsMap] = useState({});
  const [remarksMap, setRemarksMap] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingPage, setSavingPage] = useState(null);

  const token = useSelector((state) => state.auth?.token) || localStorage.getItem('token');
  const userRole = useSelector((state) => state.auth?.role) || localStorage.getItem('role');
  const navigate = useNavigate();

  useEffect(() => {
    // Restrict access to superadmin only
    if (userRole !== 'superadmin') {
      message.error('Access Denied: Only Super Admin can access Report Configurations.');
      navigate('/dashboard');
    }
  }, [userRole, navigate]);

  useEffect(() => {
    const fetchIECs = async () => {
      setLoadingIECs(true);
      try {
        const res = await getAllAdminIECsService(token);
        if (res?.data) setIecList(res.data);
      } catch (e) { console.error(e); }
      finally { setLoadingIECs(false); }
    };
    fetchIECs();
  }, [token]);

  useEffect(() => {
    if (!selectedIEC) return;
    const fetchConfig = async () => {
      setLoadingConfig(true);
      try {
        const res = await getPageConfigService(selectedIEC);
        const newMap = {};
        const newRemarksMap = {};
        if (res?.config) {
          Object.keys(res.config).forEach((page) => {
            newMap[page] = {};
            newRemarksMap[page] = {};
            Object.keys(res.config[page]).forEach((key) => {
              if (key.includes('thresholds')) {
                const tStr = res.config[page][key];
                newMap[page][key] = tStr.split(',').map(x => parseInt(x.trim(), 10)).filter(x => !isNaN(x));
              }
              if (key.includes('remarks')) {
                const rStr = res.config[page][key];
                newRemarksMap[page][key] = rStr.split('|');
              }
            });
          });
        }
        setThresholdsMap(newMap);
        setRemarksMap(newRemarksMap);
      } catch (e) { console.error(e); setThresholdsMap({}); setRemarksMap({}); }
      finally { setLoadingConfig(false); }
    };
    fetchConfig();
  }, [selectedIEC]);

  const getKey = (page_name, mode) => mode ? `thresholds_${mode}` : 'thresholds';
  const getRemarksKey = (page_name, mode) => mode ? `remarks_${mode}` : 'remarks';

  const getThresholds = (page_name, mode, defaults) => {
    const key = getKey(page_name, mode);
    return (thresholdsMap[page_name] && thresholdsMap[page_name][key]) || defaults;
  };

  const getRemarks = (page_name, mode, defaultsLength) => {
    const key = getRemarksKey(page_name, mode);
    const stored = remarksMap[page_name] && remarksMap[page_name][key];
    if (stored && stored.length >= defaultsLength) return stored;
    return Array(defaultsLength).fill('');
  };

  const updateThreshold = (page_name, mode, index, val, defaults) => {
    const key = getKey(page_name, mode);
    const current = [...getThresholds(page_name, mode, defaults)];
    current[index] = val;
    setThresholdsMap(prev => ({
      ...prev,
      [page_name]: {
        ...(prev[page_name] || {}),
        [key]: current
      }
    }));
  };

  const addThreshold = (page_name, mode, defaults) => {
    const key = getKey(page_name, mode);
    const current = [...getThresholds(page_name, mode, defaults)];
    current.push((current[current.length - 1] || 0) + 10);
    setThresholdsMap(prev => ({
      ...prev,
      [page_name]: {
        ...(prev[page_name] || {}),
        [key]: current
      }
    }));
  };

  const removeThreshold = (page_name, mode, index, defaults) => {
    const key = getKey(page_name, mode);
    const rKey = getRemarksKey(page_name, mode);
    const current = [...getThresholds(page_name, mode, defaults)];
    if (current.length <= 1) { message.warning('At least one threshold is required.'); return; }
    current.splice(index, 1);

    // Also remove the corresponding remark if it exists
    const currentRemarks = [...getRemarks(page_name, mode, current.length + 2)];
    if (currentRemarks.length > index) {
      currentRemarks.splice(index, 1);
    }

    setThresholdsMap(prev => ({
      ...prev,
      [page_name]: {
        ...(prev[page_name] || {}),
        [key]: current
      }
    }));
    setRemarksMap(prev => ({
      ...prev,
      [page_name]: {
        ...(prev[page_name] || {}),
        [rKey]: currentRemarks
      }
    }));
  };

  const updateRemark = (page_name, mode, index, val, binsCount) => {
    const key = getRemarksKey(page_name, mode);
    const current = [...getRemarks(page_name, mode, binsCount)];
    current[index] = val;
    setRemarksMap(prev => ({
      ...prev,
      [page_name]: {
        ...(prev[page_name] || {}),
        [key]: current
      }
    }));
  };

  const handleSave = async (page_name, modes, defaults) => {
    if (!selectedIEC) { message.warning('Please select an IEC first.'); return; }

    const configsToSave = [];
    const modeList = modes && modes.length > 0 ? modes : [null];

    for (const mode of modeList) {
      const current = getThresholds(page_name, mode, defaults);
      for (let i = 0; i < current.length - 1; i++) {
        if (current[i] >= current[i + 1]) {
          message.error(`Thresholds for ${mode || ''} must be in strictly ascending order.`);
          return;
        }
      }
      configsToSave.push({
        config_key: getKey(page_name, mode),
        config_value: current.join(','),
        config_type: 'string'
      });

      // Also save remarks
      const expectedBinsCount = current.length + 2; // For TAT: N thresholds -> N+1 bins + 1 'Not cleared' bin
      const rKey = getRemarksKey(page_name, mode);
      const currentRemarks = remarksMap[page_name] && remarksMap[page_name][rKey];
      if (currentRemarks) {
        // Fill empty strings if array is shorter than expected bins
        const remarksToSave = [...currentRemarks];
        while (remarksToSave.length < expectedBinsCount) {
          remarksToSave.push('');
        }
        configsToSave.push({
          config_key: rKey,
          config_value: remarksToSave.slice(0, expectedBinsCount).join('|'),
          config_type: 'string'
        });
      }
    }

    setSavingPage(page_name);
    try {
      await savePageConfigService(selectedIEC, page_name, configsToSave);
      message.success(`"${page_name}" saved successfully!`);
    } catch (e) { message.error('Failed to save configuration.'); }
    finally { setSavingPage(null); }
  };

  const getBinPreview = (page_name, mode, defaults, type) => {
    const t = getThresholds(page_name, mode, defaults);
    const expectedBinsCount = type === 'TAT' ? t.length + 2 : t.length + 2;
    const r = getRemarks(page_name, mode, expectedBinsCount);
    const preview = [];
    if (type === 'TAT') {
      preview.push({ label: `0–${t[0]} days`, color: 'green', customRemark: r[0] || '' });
      for (let i = 0; i < t.length - 1; i++) preview.push({ label: `${t[i] + 1}–${t[i + 1]} days`, color: 'orange', customRemark: r[i + 1] || '' });
      preview.push({ label: `${t[t.length - 1]}+ days`, color: 'red', customRemark: r[t.length] || '' });
      preview.push({ label: 'Not cleared', color: 'default', customRemark: r[t.length + 1] || '' });
    } else {
      preview.push({ label: 'Expired', color: 'red', customRemark: r[0] || '' });
      preview.push({ label: `0–${t[0]} days`, color: 'red', customRemark: r[1] || '' });
      for (let i = 0; i < t.length - 1; i++) preview.push({ label: `${t[i] + 1}–${t[i + 1]} days`, color: i === 0 ? 'orange' : 'green', customRemark: r[i + 2] || '' });
      preview.push({ label: `${t[t.length - 1]}+ days`, color: 'green', customRemark: r[t.length + 1] || '' });
    }
    return preview;
  };

  return (
    <div style={{ padding: '0px 0px', maxWidth: 1400, margin: '0 auto', background: '#f8fafc', minHeight: '100vh' }}>

      {/* Page Title */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
          <SettingOutlined style={{ color: '#2563eb', fontSize: 30 }} />
          <span style={{ fontSize: 20, fontWeight: 800, color: '#111827', letterSpacing: '-0.02em' }}>Report Configurations</span>
        </div>
        <span style={{ fontSize: 14, color: '#64748b' }}>Manage day-bucket thresholds for TAT reports per IEC.</span>
      </div>

      {/* IEC Selector */}
      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16,
        padding: '12px 28px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 24,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)'
      }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: '#334155', whiteSpace: 'nowrap' }}>Select IEC</span>
        <div style={{ width: 1, height: 40, background: '#e2e8f0' }} />
        <Select
          showSearch
          style={{ flex: 1, maxWidth: 480 }}
          placeholder="Search by IEC code or company name..."
          loading={loadingIECs}
          value={selectedIEC}
          onChange={(val) => { setSelectedIEC(val); setThresholdsMap({}); }}
          filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          optionFilterProp="label"
          size="large"
          className="iec-selector-large"
        >
          {iecList.map((iec) => {
            const iecNo = iec.iec_no || iec;
            const name = iec.name || iec.fullname || '';
            const label = name ? `${iecNo} - ${name}` : iecNo;
            return (
              <Option key={iecNo} value={iecNo} label={label}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>{iecNo}</span>
                {name && <span style={{ color: '#64748b', marginLeft: 8, fontSize: 14 }}>— {name}</span>}
              </Option>
            );
          })}
        </Select>

      </div>

      {/* Empty state */}
      {!selectedIEC && (
        <div style={{
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
          padding: '40px 0', textAlign: 'center',
        }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>⚙️</div>
          <span style={{ color: '#9ca3af', fontSize: 16 }}>Select an IEC above to configure its report settings.</span>
        </div>
      )}

      {/* Side-by-side cards */}
      {selectedIEC && (
        <Spin spinning={loadingConfig}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 22, alignItems: 'start' }}>
            {REPORT_CONFIGS.map(({ page_name, label, description, defaultThresholds, type, accentColor, modes }) => {
              const modeList = modes && modes.length > 0 ? modes : [null];

              return (
                <div key={page_name} style={{
                  background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
                  overflow: 'hidden',
                }}>
                  {/* Header */}
                  <div style={{
                    borderBottom: '1px solid #f1f5f9', padding: '18px 20px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20,
                  }}>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 16, color: '#1e293b' }}>{label}</div>
                      <div style={{ fontSize: 14, color: '#64748b', marginTop: 6 }}>{description}</div>
                    </div>
                    <Button
                      type="primary"
                      icon={<SaveOutlined style={{ fontSize: 14 }} />}
                      loading={savingPage === page_name}
                      onClick={() => handleSave(page_name, modes, defaultThresholds)}
                      style={{
                        background: accentColor, borderColor: accentColor, borderRadius: 10,
                        flexShrink: 0, height: 40, padding: '0 24px', fontSize: 14, fontWeight: 600
                      }}
                    >
                      Save All
                    </Button>
                  </div>

                  {/* Body */}
                  <div style={{ padding: '8px 28px 28px' }}>
                    <Tabs
                      defaultActiveKey="Sea"
                      type="card"
                      className="premium-tabs-large"
                      items={modeList.map((mode) => ({
                        key: mode || 'default',
                        label: <span style={{ fontSize: 14, fontWeight: 600 }}>{mode || 'Standard'}</span>,
                        children: (
                          <div style={{ paddingTop: 20 }}>
                            <div style={{ fontSize: 14, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', marginBottom: 18 }}>
                              Threshold Bins {mode ? `(${mode})` : ''}
                            </div>

                            {(getThresholds(page_name, mode, defaultThresholds) || []).map((val, idx, tList) => (
                              <div key={idx} style={{
                                display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14,
                                padding: '8px 20px', background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                              }}>
                                <div style={{
                                  width: 40, height: 30, borderRadius: 10,
                                  background: accentColor + '10', color: accentColor,
                                  fontSize: 14, fontWeight: 800,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                  {idx + 1}
                                </div>
                                <span style={{ fontSize: 14, color: '#334155', fontWeight: 600, flex: 1 }}>Maximum days</span>
                                <InputNumber
                                  min={1} max={9999} value={val}
                                  onChange={(v) => updateThreshold(page_name, mode, idx, v, defaultThresholds)}
                                  style={{ width: 110, borderRadius: 8, height: 30, fontSize: 14, display: 'flex', alignItems: 'center' }}
                                />
                                <Button
                                  type="text" danger icon={<DeleteOutlined style={{ fontSize: 14 }} />}
                                  onClick={() => removeThreshold(page_name, mode, idx, defaultThresholds)}
                                  disabled={tList.length <= 1}
                                  style={{ padding: '0 12px', height: 42, borderRadius: 8 }}
                                />
                              </div>
                            ))}

                            <Button
                              type="dashed" icon={<PlusOutlined style={{ fontSize: 14 }} />} block
                              onClick={() => addThreshold(page_name, mode, defaultThresholds)}
                              style={{
                                borderRadius: 12, marginTop: 10, height: 40, fontSize: 14, fontWeight: 600,
                                borderColor: accentColor + '40', color: accentColor, background: accentColor + '05'
                              }}
                            >
                              Add New Bin
                            </Button>

                            {/* Preview Section */}
                            <div style={{
                              marginTop: 32, padding: '24px', background: '#f8fafc',
                              borderRadius: 16, border: '1px solid #e2e8f0',
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                                <div style={{ width: 5, height: 20, background: accentColor, borderRadius: 3 }} />
                                <span style={{ fontSize: 14, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b' }}>
                                  Remarks Preview
                                </span>
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                                {getBinPreview(page_name, mode, defaultThresholds, type).map(({ label: pLabel, color, customRemark }, bIdx, bList) => {
                                  const c = chipColors[color] || chipColors.default;
                                  return (
                                    <div key={pLabel} style={{
                                      display: 'flex', flexDirection: 'column', gap: 8,
                                      background: c.bg, border: `1px solid ${c.border}`, borderRadius: 10,
                                      padding: '12px', minWidth: '180px', flex: '1 1 calc(25% - 10px)'
                                    }}>
                                      <span style={{ fontSize: 13, fontWeight: 800, color: c.text, textTransform: 'uppercase' }}>
                                        {pLabel}
                                      </span>
                                      <Input
                                        placeholder="Default Remark"
                                        value={customRemark}
                                        onChange={(e) => updateRemark(page_name, mode, bIdx, e.target.value, bList.length)}
                                        style={{ borderRadius: 6 }}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        )
                      }))}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Spin>
      )}
    </div>
  );
};

export default PageConfigurator;